import { Icon } from "@circle-react-shared/Icon";
import { Typography } from "@circle-react-uikit/Typography";
import { Button } from "@circleco/compass/components/Button";

import { t } from "@/i18n-js/instance";

import { PLAN_ACTIONS, isAnyUpgrade } from "./planActionHelpers";
import { PlanFeaturesList } from "./PlanDetails/PlanFeaturesList";
import { useUpgradePlanContext } from "./UpgradePlanContext";
import { UpgradePlanModalBody } from "./UpgradePlanModalBody";
import { UpgradePlanModalFooter } from "./UpgradePlanModalFooter";

const i18nRoot = "pricing_plans.upgrade_modal.post_plan_update";

export interface PlanUpdateSuccessProps {
  buttonLabel?: string;
  onSuccess: () => void;
}

type PlanFeatures = string[];

const getFeaturesToDisplay = (
  planAction: string,
  isEmailHubOnlyUpgrade: boolean,
  emailHubPlan: { main_features?: PlanFeatures } | undefined,
  mainFeatures: PlanFeatures,
  featuresLostFromCurrentPlan: PlanFeatures,
): PlanFeatures => {
  if (planAction === PLAN_ACTIONS.UPGRADE) {
    return isEmailHubOnlyUpgrade && emailHubPlan?.main_features
      ? emailHubPlan.main_features
      : mainFeatures;
  }

  if (planAction === PLAN_ACTIONS.DOWNGRADE) {
    return featuresLostFromCurrentPlan;
  }

  return [];
};

const getDisplayName = (
  isEmailHubOnlyUpgrade: boolean,
  emailHubPlan: { display_name?: string } | undefined,
  planDisplayName: string,
): string =>
  isEmailHubOnlyUpgrade && emailHubPlan?.display_name
    ? emailHubPlan.display_name
    : planDisplayName;

export const PlanUpdateSuccess = ({
  buttonLabel,
  onSuccess,
}: PlanUpdateSuccessProps) => {
  const { plan, emailHubPlan, isEmailHubOnlyUpgrade } = useUpgradePlanContext();
  const planAction = plan?.plan_action || PLAN_ACTIONS.UPGRADE;

  const planDisplayName = plan?.display_name || "";
  const mainFeatures = plan?.main_features || [];
  const featuresLostFromCurrentPlan =
    plan?.features_lost_from_current_plan || [];

  const isUpgrade = isAnyUpgrade(planAction);
  const displayName = getDisplayName(
    isEmailHubOnlyUpgrade,
    emailHubPlan,
    planDisplayName,
  );

  const header = isEmailHubOnlyUpgrade
    ? t([i18nRoot, "header", "activate_email_hub_success"])
    : t([i18nRoot, "header", planAction || PLAN_ACTIONS.UPGRADE]);
  const subheading = isEmailHubOnlyUpgrade
    ? t([i18nRoot, "subheading", "activate_email_hub_success"])
    : t([i18nRoot, "subheading", planAction || PLAN_ACTIONS.UPGRADE], {
        plan: displayName,
      });

  const features = getFeaturesToDisplay(
    planAction,
    isEmailHubOnlyUpgrade,
    emailHubPlan,
    mainFeatures,
    featuresLostFromCurrentPlan,
  );

  return (
    <>
      <UpgradePlanModalBody>
        <div className="mt-16 text-center">
          {isUpgrade && (
            <Icon type="spaceship-blue" size={56} className="mb-10 !w-14" />
          )}

          <Typography.TitleLg color="text-darkest">{header}</Typography.TitleLg>

          <div className="mt-3">
            <Typography.BodyMd
              color="text-default"
              weight="normal"
              size="text-md"
            >
              {subheading}
            </Typography.BodyMd>
          </div>
          {features.length > 0 && (
            <div className="dark:bg-primary mt-6 rounded-md bg-slate-50 px-6 py-5 text-left">
              <PlanFeaturesList
                features={features}
                iconType={isUpgrade ? "check" : "16-close"}
                iconColor={isUpgrade ? "text-green-success" : "text-red-500"}
              />
            </div>
          )}
        </div>
      </UpgradePlanModalBody>
      <UpgradePlanModalFooter
        className="mt-5"
        layout="full-width"
        primaryAction={{ label: buttonLabel || "", onClick: onSuccess }}
      >
        <Button variant="primary" size="lg" fullWidth onClick={onSuccess}>
          {buttonLabel}
        </Button>
      </UpgradePlanModalFooter>
    </>
  );
};
